import React from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  OrderDetailsOverviewCard,
  OrderDetailsCard,
  CustomerDetailsCard,
  OrderDetailTabs,
  OrderDetailsTimeline,
} from "../../components";
import { BackButton } from "../../../../components";
import {
  getInvoices,
  resetInvoices,
  getOrder,
  getOrderDetails,
  resetOrder,
  resetSendEmailConfirmation,
} from "../../ordersSlice";

import "./OrderDetails.scss";

// Temporary rollout switch between the legacy card layout and the new
// lifecycle-timeline layout. Persisted so a chosen view sticks across orders
// and refreshes. Remove once the new layout is the permanent default.
const UI_PREF_KEY = "orderDetailsUi";
type UiVersion = "legacy" | "timeline";
function readUiPref(): UiVersion {
  try {
    return localStorage.getItem(UI_PREF_KEY) === "legacy" ? "legacy" : "timeline";
  } catch {
    return "timeline";
  }
}

export function OrderDetails() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const orderId = params.id;

  const getOrderAPI = useAppSelector((state) => state.orders.getOrder);
  const createWeightOverageInvoiceAPI = useAppSelector(
    (state) => state.orders.createWeightOverageInvoiceAPI
  );
  const createCustomInvoiceAPI = useAppSelector(
    (state) => state.orders.createCustomInvoiceAPI
  );
  const order = useAppSelector((state) => state.orders.order);
  const orderDetails = useAppSelector((state) => state.orders.orderDetails);

  const [uiVersion, setUiVersion] = React.useState<UiVersion>(readUiPref);
  const chooseUi = (v: UiVersion) => {
    setUiVersion(v);
    try {
      localStorage.setItem(UI_PREF_KEY, v);
    } catch {
      /* ignore storage failures (private mode) */
    }
  };

  React.useEffect(() => {
    if (!orderId) return;
    dispatch(getOrder(orderId));
    dispatch(getOrderDetails(orderId));
    dispatch(getInvoices({ orderId }));
    dispatch(resetSendEmailConfirmation());

    return () => {
      dispatch(resetInvoices());
      dispatch(resetOrder());
    };
  }, [orderId, dispatch]);

  React.useEffect(() => {
    if (!orderId) return;

    if (!createCustomInvoiceAPI.loading && !!createCustomInvoiceAPI.value) {
      dispatch(getInvoices({ orderId }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createCustomInvoiceAPI.loading, createCustomInvoiceAPI.value]);

  React.useEffect(() => {
    if (!orderId) return;

    if (
      !createWeightOverageInvoiceAPI.loading &&
      !!createWeightOverageInvoiceAPI.value
    ) {
      dispatch(getInvoices({ orderId }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createWeightOverageInvoiceAPI.loading,
    createWeightOverageInvoiceAPI.value,
  ]);

  const onBackButtonClick = () => {
    navigate(-1);
  };

  return (
    <div className="order-details-component">
      <div className="back-button-container">
        <BackButton onClick={onBackButtonClick} />
        <div className="ui-version-toggle" role="group" aria-label="Layout">
          <button
            type="button"
            className={uiVersion === "timeline" ? "active" : ""}
            aria-pressed={uiVersion === "timeline"}
            onClick={() => chooseUi("timeline")}
          >
            New UI
          </button>
          <button
            type="button"
            className={uiVersion === "legacy" ? "active" : ""}
            aria-pressed={uiVersion === "legacy"}
            onClick={() => chooseUi("legacy")}
          >
            Old UI
          </button>
        </div>
      </div>

      {getOrderAPI.loading ? (
        <div className="loading">Loading...</div>
      ) : uiVersion === "timeline" ? (
        <>
          {order && (
            <OrderDetailsTimeline order={order} orderDetails={orderDetails} />
          )}
        </>
      ) : (
        <>
          <OrderDetailsOverviewCard />
          {order && <CustomerDetailsCard order={order} />}
          {order && <OrderDetailsCard order={order} orderDetails={orderDetails} />}
          {order && <OrderDetailTabs order={order} />}
        </>
      )}
    </div>
  );
}
